
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Pitch } from '@/types/pitch';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ThumbsUp, MessageSquare, CalendarIcon } from 'lucide-react';

interface PitchCardProps {
  pitch: Pitch;
  onUpvote: (id: string) => void;
  onSelect: (pitch: Pitch) => void;
}

const PitchCard: React.FC<PitchCardProps> = ({ pitch, onUpvote, onSelect }) => {
  const categoryColors = {
    business: 'bg-blue-100 text-blue-800',
    social_impact: 'bg-green-100 text-green-800',
    creative: 'bg-purple-100 text-purple-800',
    other: 'bg-gray-100 text-gray-800'
  };

  const categoryColor = categoryColors[pitch.category];
  const formattedDate = formatDistanceToNow(new Date(pitch.created_at), { addSuffix: true });
  
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <CardHeader className="pb-3 space-y-1">
        <div className="flex justify-between items-start">
          <div className="flex gap-3 items-center">
            <Avatar>
              <AvatarImage src={pitch.author?.avatar_url || ""} />
              <AvatarFallback className="bg-primary/20">
                {pitch.author?.username?.[0] || pitch.author?.full_name?.[0] || "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-semibold">
                {pitch.author?.username || pitch.author?.full_name || "Anonymous"}
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarIcon className="h-3 w-3" /> {formattedDate}
              </div>
            </div>
          </div>
          <Badge className={`${categoryColor}`}>
            {pitch.category.replace('_', ' ')}
          </Badge>
        </div>
        <h3 className="text-lg font-semibold mt-2">{pitch.title}</h3>
      </CardHeader>
      <CardContent className="pb-3">
        <p className="line-clamp-3">{pitch.description}</p>
      </CardContent>
      <CardFooter className="pt-0 flex justify-between">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onUpvote(pitch.id)}
          className={pitch.has_upvoted ? "text-primary" : ""}
        >
          <ThumbsUp className={`h-4 w-4 mr-1 ${pitch.has_upvoted ? "fill-primary" : ""}`} /> 
          {pitch.upvotes_count || 0}
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onSelect(pitch)}
        >
          <MessageSquare className="h-4 w-4 mr-1" /> Feedback
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PitchCard;
